// Thin React wrapper around Cloudflare's Turnstile JS. Loads the
// upstream script once, mounts a widget into a ref'd div, fires
// onVerify with the resulting token. The widget mode (managed,
// invisible, or interactive) is chosen by the dashboard config —
// nothing for us to set here.
//
// Site key is read from VITE_TURNSTILE_SITE_KEY at build time. In dev
// the documented test key 1x00000000000000000000AA always passes,
// so the form works without a real challenge render.

import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "auto" | "light" | "dark";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  // Re-use any in-flight or already-injected script so multiple
  // Turnstile widgets on the same page don't load it twice.
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${SCRIPT_SRC}"]`,
  );
  if (existing) {
    return new Promise((res) => {
      existing.addEventListener("load", () => res());
      if (window.turnstile) res();
    });
  }
  return new Promise((res) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.addEventListener("load", () => res());
    document.head.appendChild(s);
  });
}

export function TurnstileWidget(props: {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState(false);

  // Callbacks live in a ref so they are NOT dependencies of the render
  // effect. Callers pass inline arrows (new identity every render), and
  // with the callbacks in the dep array each parent re-render tore the
  // widget down and re-rendered it — worse, a successful silent challenge
  // called onVerify → parent state change → re-render → widget rebuilt →
  // new challenge → new token → … a feedback loop whose only stable state
  // was Turnstile escalating to the interactive checkbox. The widget must
  // mount exactly once per siteKey.
  const callbacksRef = useRef({
    onVerify: props.onVerify,
    onExpired: props.onExpired,
    onError: props.onError,
  });
  callbacksRef.current.onVerify = props.onVerify;
  callbacksRef.current.onExpired = props.onExpired;
  callbacksRef.current.onError = props.onError;

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (!cancelled) setScriptReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!scriptReady || !ref.current || !window.turnstile) return;
    const id = window.turnstile.render(ref.current, {
      sitekey: props.siteKey,
      callback: (token) => callbacksRef.current.onVerify(token),
      "expired-callback": () => callbacksRef.current.onExpired?.(),
      "error-callback": () => callbacksRef.current.onError?.(),
      theme: "auto",
    });
    widgetIdRef.current = id;
    return () => {
      try { window.turnstile?.remove(id); } catch { /* widget already gone */ }
    };
  }, [scriptReady, props.siteKey]);

  return <div ref={ref} style={{ minHeight: 65 }} />;
}
